import React, { useContext, useEffect } from 'react';

import { Form, notification, Spin } from 'antd';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import IntlMessages from 'util/IntlMessages';
import BoxContainer from 'components/BoxContainer';
import FilterContainer from 'components/FilterContainer';
import Title from 'components/BoxContainer/components/Title';
import useReportManagerDt from 'packages/utils/hooks/useReportManagerDt';

import TitleReport from '../components/TitleReport';
import { HolidaysReport } from '../components/reports';
import { DT_FORMS, KIND_OF_REPORTS, PATHS } from '../../constants';
import TimeTrackerFilters from '../components/TimeTrackerFilters';
import { useTimeTracker } from '../../contexts/TimeTrackerProvider';
import useTimeTrackerReportFactory from '../../hooks/useTimeTrackerReportFactory';
import { getReportDownloadMethods } from '../utils/reportDownloadFactory';
import ReportExportButtons from '../components/ReportExportButtons';
import { ReportContext } from '../contexts/ReportContext';
import { useEmptyUserDataExportParams } from '../hooks/useEmptyUserDataExportParams';

export default function Holidays() {
  const [preparingFile, setPreparingFile] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const { selectedIndividualUser, setSelectedIndividualUser } = useContext(ReportContext);
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const isFilterMode = location.search === '';
  const [form] = Form.useForm();
  const {
    renderReport,
    onSubmit,
    data,
    next,
    loading: reportLoading,
    queriedPeriod,
    error: errorReport,
  } = useReportManagerDt();

  const { corporation, loading } = useTimeTracker();
  const { data: dataParsed, error } = useTimeTrackerReportFactory({
    data: data,
    reportType: KIND_OF_REPORTS.HOLIDAYS,
    queriedPeriod,
    reset: !renderReport,
  });
  const hasErrors = error || errorReport;

  const downloadMethods = getReportDownloadMethods(KIND_OF_REPORTS.HOLIDAYS);
  const { emptyUserDataLines } = useEmptyUserDataExportParams({
    reportType: PATHS.HOLIDAYS,
    reportData: dataParsed,
    queriedPeriod,
  });

  const reportTitle = intl.formatMessage({ id: 'work.direction.holidays.report' });
  const pdfParams = {
    data: dataParsed.data,
    title: reportTitle,
    fileName: reportTitle,
    orientation: 'landscape',
    emptyUserDataLines,
  };

  const onClearFilters = () => {
    form.resetFields();
    setRefresh(prev => !prev);
  };

  const onHashSubmit = () => {
    const values = form.getFieldsValue();
    const { hash } = values;

    if (!hash) {
      form.setFields([
        {
          name: 'hash',
          errors: [intl.formatMessage({ id: 'work.direction.filter.hash.validation.empty.error' })],
        },
      ]);
      return;
    }

    const currentPath = location.pathname;
    const basePath = currentPath.substring(0, currentPath.lastIndexOf('/'));
    history.push(`${basePath}/hash?value=${encodeURIComponent(hash)}`);
  };

  const mainLoading = loading || reportLoading;

  useEffect(() => {
    if (renderReport && hasErrors) {
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
    }
    if (!renderReport && selectedIndividualUser) {
      setSelectedIndividualUser(null);
    }
  }, [renderReport, hasErrors]);

  return (
    <BoxContainer fullContent>
      <BoxContainer fixed>
        <FilterContainer
          titleClassName="gx-w-100"
          goBack={renderReport ? history.goBack : null}
          title={
            <Title.Header
              value={
                <div className="gx-flex-row gx-justify-content-between gx-align-items-center">
                  <TitleReport
                    corporationName={corporation?.name}
                    title={<IntlMessages id="work.direction.holidays.report" />}
                    rut={corporation?.RUT}
                  />

                  {renderReport && (
                    <ReportExportButtons
                      downloadMethods={downloadMethods}
                      pdfParams={pdfParams}
                      onDownloadDocx={() =>
                        downloadMethods.downloadDocx({
                          data: dataParsed,
                          fileName: 'holidays',
                          reportTitle,
                          emptyUserDataLines,
                        })
                      }
                      onDownloadExcel={() =>
                        downloadMethods.downloadExcel({
                          data: dataParsed,
                          fileName: 'holidays',
                          reportTitle,
                          emptyUserDataLines,
                        })
                      }
                      preparingFile={preparingFile}
                      setPreparingFile={setPreparingFile}
                    />
                  )}
                </div>
              }
            />
          }
          actionButtons={
            isFilterMode && [
              {
                label: <IntlMessages id="work.direction.filter.clear" />,
                action: onClearFilters,
                className: 'gx-mr-2',
              },
            ]
          }
        />
      </BoxContainer>
      <BoxContainer fullContent onErrorCallback={history.goBack} error={hasErrors}>
        <Spin spinning={reportLoading}>
          {!renderReport ? (
            <>
              <TimeTrackerFilters
                onSubmit={onSubmit}
                formName={DT_FORMS.HOLIDAYS_REPORT}
                form={form}
                refresh={refresh}
                onHashSubmit={onHashSubmit}
              />
            </>
          ) : (
            <HolidaysReport
              data={dataParsed}
              next={next}
              loading={mainLoading}
              queriedPeriod={queriedPeriod}
            />
          )}
        </Spin>
      </BoxContainer>
    </BoxContainer>
  );
}
